import { useNavigate } from 'react-router-dom'
import { Package } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useLots, type LotRow } from '../api/gate'
import { useCrops, useUser } from '../api/reference'
import { FilterBar, SearchFilter, SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { PersonPicker } from '../components/PersonPicker'
import { Code, Kg, Money, RefLink, Status, Stream, When } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'

const LIMIT = 25

/** Lots — F4.10-e/k · F5.6: filter by session, status, hold, farmer, stream, crop, paper day. */
export function LotsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({
    code: '',
    status: '',
    hold: '',
    stream: '',
    cropId: '',
    farmerId: '',
    sessionId: '',
    paperDay: '',
    page: 1,
  })
  const crops = useCrops()
  const farmer = useUser(f.farmerId || null)
  const q = useLots({
    page: f.page,
    limit: LIMIT,
    code: f.code || undefined,
    status: f.status || undefined,
    hold: (f.hold || undefined) as never,
    stream: (f.stream || undefined) as never,
    cropId: f.cropId || undefined,
    farmerId: f.farmerId || undefined,
    sessionId: f.sessionId || undefined,
    paperDay: f.paperDay === '' ? undefined : f.paperDay === 'true',
  })

  const columns: Column<LotRow>[] = [
    { id: 'code', header: 'Lot', cell: (r) => <Code>{r.code}</Code> },
    {
      id: 'farmer',
      header: 'Farmer',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.farmerName ?? '—'}</span>
          {r.rosterNo && <span className="font-mono text-[11.5px] text-muted">{r.rosterNo}</span>}
        </span>
      ),
    },
    { id: 'crop', header: 'Crop', cell: (r) => r.cropName ?? '—', hideBelow: 'lg' },
    { id: 'stream', header: 'Stream', cell: (r) => <Stream stream={r.stream} />, hideBelow: 'md' },
    {
      id: 'session',
      header: 'Day',
      cell: (r) => (r.sessionId ? <RefLink to={`/c/sessions/${r.sessionId}`}>{r.sessionCode ?? 'day'}</RefLink> : '—'),
      hideBelow: 'lg',
    },
    { id: 'bags', header: 'Bags', align: 'right', mono: true, cell: (r) => r.bagCount, hideBelow: 'sm' },
    { id: 'net', header: 'Net', align: 'right', cell: (r) => <Kg grams={r.netG} /> },
    { id: 'amount', header: 'Value', align: 'right', cell: (r) => <Money paise={r.amountPaise} />, hideBelow: 'md' },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1">
          <Status status={r.status} />
          {r.hold !== 'NONE' && <Status status={r.hold} label={r.holdReasons[0] ? `Hold · ${r.holdReasons[0]}` : 'On hold'} />}
          {r.paperDay && <Status status="PAPER" />}
        </span>
      ),
    },
    { id: 'committed', header: 'Committed', cell: (r) => <When at={r.committedAt} />, hideBelow: 'lg' },
  ]

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Lots"
      description="Farmer lots from the gate: good and ordinary streams, holds and their reasons, paper days."
    >
      <DataTable<LotRow>
        caption="Lots"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/lots/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Lot code" value={f.code} onChange={(code) => setF({ code })} placeholder="e.g. HL-1018" />
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={['DRAFT', 'COMMITTED', 'IN_TRANSIT', 'STORED', 'VOID'].map((s) => ({ value: s, label: s.replace('_', ' ').toLowerCase() }))}
            />
            <SelectFilter
              label="Hold"
              value={f.hold}
              onChange={(hold) => setF({ hold })}
              options={[
                { value: 'NONE', label: 'Not on hold' },
                { value: 'QUARANTINE', label: 'On hold' },
                { value: 'HELD', label: 'Held (release)' },
                { value: 'RECALLED', label: 'Recalled' },
              ]}
            />
            <SelectFilter
              label="Stream"
              value={f.stream}
              onChange={(stream) => setF({ stream })}
              options={[
                { value: 'MAIN', label: 'Good' },
                { value: 'CONV', label: 'Ordinary' },
              ]}
            />
            <SelectFilter label="Crop" value={f.cropId} onChange={(cropId) => setF({ cropId })} options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <SelectFilter
              label="Paper day"
              value={f.paperDay as '' | 'true' | 'false'}
              onChange={(paperDay) => setF({ paperDay })}
              options={[
                { value: 'true', label: 'Paper days' },
                { value: 'false', label: 'App days' },
              ]}
              allLabel="Any"
            />
            <div className="flex min-w-[14rem] flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">Farmer</span>
              <PersonPicker
                label="Farmer"
                value={f.farmerId ? (farmer.data ?? { id: f.farmerId, name: '…', username: '', farmerCode: null, contactNo: null, roles: [] }) : null}
                onChange={(u) => setF({ farmerId: u?.id ?? '' })}
                placeholder="Name or roster no."
              />
            </div>
            {f.sessionId && (
              <button type="button" className="h-9 rounded-chip border border-rule px-2 font-mono text-[11px]" onClick={() => setF({ sessionId: '' })}>
                Day filter ✕
              </button>
            )}
          </FilterBar>
        }
        empty={<EmptyState icon={<Package />} title="No lots match" description="Lots appear when a delivery is committed at the gate." />}
      />
    </ConsolePage>
  )
}
